import type { PanInfo } from 'motion/react'
import type { Ingredient } from "@/types"

import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from "react-router-dom"

import { Check, Plus, Trash2, ArrowLeft } from 'lucide-react'
import { motion, useDragControls, useMotionValue, animate } from 'motion/react'

import { Checkbox, ConnectionBanner, IngredientDialog, IngredientMarkingTooltip, Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui"
import { MixLayout } from "@/components/layout"
import { useWebSocketContext } from "@/contexts"
import { useKeydownShortcut } from "@/hooks"
import { groupIngredientsByCategory, sortIngredientsByCategoryAndName } from "@/lib/ingredientCategories"
import { getCategoryImage } from '@/lib/categoryImages'

export default function GroceryListPage() {
  const { mixId = '' } = useParams()
  const { deleteIngredient, getSelectedRecipesIngredients, isMix, loading, markIngredient } = useWebSocketContext()
  const [ingredientDialogOpen, setIngredientDialogOpen] = useState(false);
  const [openItems, setOpenItems] = useState<string[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !isMix(mixId)) {
      navigate('/home')
    }
  }, [])

  useKeydownShortcut(
    { key: 'i', ctrl: false, meta: false, alt: false },
    () => setIngredientDialogOpen(true),
    'Open Ingredient Dialog',
    'Open the ingredient dialog'
  );

  const handleAccordionChange = (values: string[]) => {
    setOpenItems(values)
  }

  const handleToggleMark = (ingredient: Ingredient) => {
    markIngredient(mixId, ingredient.id, 'SELFCART', ingredient.mealSlotId, ingredient.scheduledAt)
  }

  const handleDeleteIngredient = async (ingredientId: string, mealSlotId?: string | null, scheduledAt?: string | null) => {
    deleteIngredient(mixId, ingredientId, mealSlotId, scheduledAt)
  }

  const SwipeableIngredient = ({ ingredient, children, onToggleMark }: { ingredient: Ingredient; children: React.ReactNode; onToggleMark: (ingredient: Ingredient) => void }) => {
    const dragControls = useDragControls()
    const x = useMotionValue(0)
    const [hasDragged, setHasDragged] = useState(false)
    const [hasMoved, setHasMoved] = useState(false)
    const hasLongPressedRef = useRef(false)
    const pointerDownRef = useRef<{ x: number; y: number } | null>(null)
    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const initialXRef = useRef<number>(0)
    const ref = useRef<HTMLDivElement>(null)
    const [elementWidth, setElementWidth] = useState(0)

    const measureWidth = () => {
      if (ref.current) {
        setElementWidth(ref.current.offsetWidth)
      }
    }

    useEffect(() => {
      measureWidth()
      window.addEventListener('resize', measureWidth)
      return () => {
        window.removeEventListener('resize', measureWidth)
        // Cleanup long press timer on unmount
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current)
          longPressTimerRef.current = null
        }
      }
    }, [])

    const handlePointerDown = (event: React.PointerEvent) => {
      // Reset flags
      setHasMoved(false)
      setHasDragged(false)
      hasLongPressedRef.current = false
      // Store initial position to track movement
      pointerDownRef.current = { x: event.clientX, y: event.clientY }
      initialXRef.current = x.get()

      // Start long press timer (300ms) - if still pressed after 300ms, it's not a tap
      longPressTimerRef.current = setTimeout(() => {
        hasLongPressedRef.current = true
      }, 300)
    }

    const handleContextMenu = (event: React.MouseEvent) => {
      // Prevent the native context menu
      event.preventDefault()
    }

    const handleDragStart = () => {
      setHasDragged(true)
      setHasMoved(true)
      // Clear long press timer when drag starts
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      pointerDownRef.current = null
    }

    const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, { offset, velocity }: PanInfo) => {
      // Clear long press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      pointerDownRef.current = null

      const isLeftSwipe = offset.x < 0
      const swipeDistance = Math.abs(offset.x)
      const deleteThresholdMet = swipeDistance >= elementWidth * 0.5
      const hasHighVelocity = velocity.x < -500
      const minimumSwipeMet = swipeDistance > elementWidth * 0.3

      if (isLeftSwipe && (deleteThresholdMet || (hasHighVelocity && minimumSwipeMet))) {
        handleDeleteIngredient(ingredient.id, ingredient.mealSlotId, ingredient.scheduledAt)
      } else {
        animate(x, initialXRef.current, { type: 'spring', stiffness: 300, damping: 30 })
      }

      // Reset drag state after animation completes
      setTimeout(() => setHasDragged(false), 100)
    }

    const handleTap = () => {
      // Only trigger tap action if this wasn't a drag gesture, long press, or pointer movement
      if (!hasDragged && !hasLongPressedRef.current && !hasMoved) {
        onToggleMark(ingredient)
      }
      // Reset flags
      setTimeout(() => {
        setHasDragged(false)
        hasLongPressedRef.current = false
        setHasMoved(false)
      }, 100)
    }

    // Only disable drag if ingredient is from a recipe
    const canSwipe = ingredient.sourceType !== 'RECIPE'

    return (
      <div className="relative w-full overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-red-600 dark:bg-red-700 flex items-center pr-4 justify-end pointer-events-none z-0">
          <div className="flex items-center justify-center w-10 h-10">
            <Trash2 className="text-background w-5 h-5 inline" />
          </div>
        </motion.div>

        <motion.div
          ref={ref}
          drag={canSwipe && !ingredient.markedAt ? "x" : false}
          dragControls={dragControls}
          dragListener={true}
          dragConstraints={{ left: -1000, right: 0 }}
          dragElastic={false}
          onPointerDown={handlePointerDown}
          onDragStart={handleDragStart}
          onDragEnd={canSwipe && !ingredient.markedAt ? handleDragEnd : undefined}
          onTap={handleTap}
          onContextMenu={handleContextMenu}
          onContextMenuCapture={handleContextMenu}
          className="w-full select-none relative z-10 bg-card h-full border-t border-border"
          whileTap={{ cursor: 'grabbing' }}
          style={{ x, WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
        >
          {children}
        </motion.div>
      </div>
    )
  }

  // Group and sort ingredients by category
  const ingredients = getSelectedRecipesIngredients(mixId)
  const groupedIngredients = groupIngredientsByCategory(ingredients)
  const sortedGroupedIngredients = sortIngredientsByCategoryAndName(groupedIngredients)

  useEffect(() => {
    const allItemValues = Object.keys(sortedGroupedIngredients).reduce((acc: string[], category: string) => {
      const categoryIngredients = sortedGroupedIngredients[category]
      const hasAllCategoriesMarked = categoryIngredients.filter((ingredient) => ingredient.markedAt)

      if (categoryIngredients.length !== hasAllCategoriesMarked.length) {
        acc.push(`category: ${category}`)
      }

      return acc
    }, [])

    setOpenItems(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(allItemValues)) {
        return allItemValues
      }

      return prev
    })
  }, [ingredients.length])

  return (

      <MixLayout>
        <div className="sticky top-0 bg-background z-30">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => navigate(-1)}
              className="h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer flex-shrink-0"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-foreground truncate">Grocery List</h2>
              {ingredients.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {ingredients.filter(i => !!i.markedAt).length} marked • {ingredients.filter(i => !i.markedAt).length} remaining
                </p>
              )}
            </div>
            <button
              onClick={() => setIngredientDialogOpen(true)}
              className={`h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer flex-shrink-0`}
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <ConnectionBanner />
        </div>

        {ingredients.length > 0 ? (
          <Accordion
            className="w-full flex flex-col gap-3 px-4"
            onValueChange={handleAccordionChange}
            type="multiple"
            value={openItems}
          >
              {Object.entries(sortedGroupedIngredients).map(([category, categoryIngredients]) => {
                const isComplete = categoryIngredients.every((i) => !!i.markedAt)
                return (
                <AccordionItem key={category} className={`rounded-xl border overflow-hidden transition-colors bg-card ${isComplete ? 'border-muted opacity-60' : 'border-border'}`} value={`category: ${category}`}>
                  <AccordionTrigger className="px-4 py-3 hover:no-underline">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-muted border border-border">
                        {(() => {
                          const catImg = getCategoryImage(category)
                          return catImg ? (
                            <img src={catImg} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-background">
                              <span className="text-sm">🍽️</span>
                            </div>
                          )
                        })()}
                      </div>
                      <span className={`flex-1 font-semibold text-left ${isComplete ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{category}</span>
                      <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                        {categoryIngredients.filter((i) => !!i.markedAt).length}/{categoryIngredients.length}
                      </span>
                      {categoryIngredients.every((i) => !!i.markedAt) && (
                        <span className="flex-shrink-0">
                          <Check className="h-4 w-4 text-green-500" />
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0 flex flex-col">
                    {categoryIngredients.map((ingredient, index) => (
                      <SwipeableIngredient key={`${ingredient.id}-${index}`} ingredient={ingredient} onToggleMark={handleToggleMark}>
                        <IngredientMarkingTooltip>
                          <div className="flex items-center gap-3 py-3 px-4 overflow-hidden select-none pointer-events-none">
                          <div className="w-12 flex justify-center flex-shrink-0">
                            <Checkbox
                              checked={!!ingredient.markedAt}
                              value={ingredient.markedAt || ''}
                              className={ingredient.markedAt ? 'opacity-60' : ''}
                            />
                          </div>
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className={`text-sm font-medium truncate ${ingredient.markedAt ? 'text-muted-foreground line-through opacity-60' : 'text-foreground'}`}>
                                {ingredient.name}
                              </span>
                              {ingredient.recipeName && (
                                <span className={`text-xs italic truncate ${ingredient.markedAt ? 'text-muted-foreground opacity-60' : 'text-muted-foreground'}`}>
                                  {ingredient.recipeName}
                                  {ingredient.mealSlotId && ` · ${ingredient.mealSlotId}`}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {ingredient.quantity && (
                                <span className="text-sm">
                                  <span className={ingredient.markedAt ? 'text-muted-foreground opacity-60' : 'text-foreground'}>
                                    {ingredient.quantity}
                                  </span>
                                  {ingredient.unit && (
                                    <span className={ingredient.markedAt ? 'text-muted-foreground opacity-60' : 'text-muted-foreground'}>
                                      {' '}{ingredient.unit}
                                    </span>
                                  )}
                                </span>
                              )}
                              {ingredient.markedAt && (
                                <Check className="h-4 w-4 text-green-500 opacity-80 flex-shrink-0" />
                              )}
                              {ingredient.markedType === 'PANTRY' && (
                                <span className="px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                                  Pantry
                                </span>
                              )}
                            </div>
                          </div>
                        </IngredientMarkingTooltip>
                      </SwipeableIngredient>
                    ))}
                  </AccordionContent>
                </AccordionItem>
                )
              })}
          </Accordion>
        ) : (
          <div className="flex flex-1 items-center justify-center px-4">
            <div className="rounded-xl border border-border bg-card p-6 text-center max-w-sm">
              <p className="text-muted-foreground">
                No recipes selected. You may add individual ingredients using the + button.
              </p>
            </div>
          </div>
        )
        }
        <IngredientDialog
          open={ingredientDialogOpen}
          onClose={() => setIngredientDialogOpen(false)}
        />
      </MixLayout >

  )
}
